import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  HardDrive,
  ShieldAlert,
  Key,
  Database,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Cpu,
  MemoryStick,
  Thermometer,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { getBackups, getPasswordHealth, getDeviceHealth } from "../lib/api";

export const HealthPage = () => {
  const [backups, setBackups] = useState([]);
  const [passwords, setPasswords] = useState([]);
  const [deviceHealth, setDeviceHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [backupsRes, passwordsRes, healthRes] = await Promise.all([
        getBackups(),
        getPasswordHealth(),
        getDeviceHealth(),
      ]);
      setBackups(backupsRes.items);
      setPasswords(passwordsRes.items);
      setDeviceHealth(healthRes.items);
    } catch (err) {
      toast.error("Failed to fetch health data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
      case "healthy":
      case "low":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "failed":
      case "critical":
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-amber-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "success":
      case "healthy":
      case "low":
        return "bg-green-100 text-green-800";
      case "failed":
      case "critical":
        return "bg-red-100 text-red-800";
      default:
        return "bg-amber-100 text-amber-800";
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const backupFailures = backups.filter((b) => b.status === "failed").length;
  const passwordsAtRisk = passwords.filter(
    (p) =>
      p.is_leaked || p.risk_level === "critical" || p.risk_level === "high",
  ).length;
  const unhealthyDevices = deviceHealth.filter(
    (d) => d.health_status !== "healthy",
  ).length;

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="health-loading"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="health-page">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
          System Health
        </h1>
        <p className="text-muted-foreground mt-1">
          Monitor backups, passwords, device health & storage
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          className="border-border shadow-sm"
          data-testid="backup-summary-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Backup Status
                </p>
                <p className="font-heading text-3xl font-bold">
                  {backups.filter((b) => b.status === "success").length}/
                  {backups.length}
                </p>
                {backupFailures > 0 && (
                  <p className="text-sm text-red-600 font-medium">
                    {backupFailures} failed
                  </p>
                )}
              </div>
              <div
                className={`p-3 rounded-full ${backupFailures > 0 ? "bg-red-100" : "bg-green-100"}`}
              >
                <Database
                  className={`w-6 h-6 ${backupFailures > 0 ? "text-red-600" : "text-green-600"}`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="border-border shadow-sm"
          data-testid="password-summary-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Password Health
                </p>
                <p className="font-heading text-3xl font-bold">
                  {passwords.filter((p) => p.risk_level === "low").length}/
                  {passwords.length}
                </p>
                {passwordsAtRisk > 0 && (
                  <p className="text-sm text-red-600 font-medium">
                    {passwordsAtRisk} at risk
                  </p>
                )}
              </div>
              <div
                className={`p-3 rounded-full ${passwordsAtRisk > 0 ? "bg-red-100" : "bg-green-100"}`}
              >
                <Key
                  className={`w-6 h-6 ${passwordsAtRisk > 0 ? "text-red-600" : "text-green-600"}`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="border-border shadow-sm"
          data-testid="device-health-summary-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Device Health
                </p>
                <p className="font-heading text-3xl font-bold">
                  {
                    deviceHealth.filter((d) => d.health_status === "healthy")
                      .length
                  }
                  /{deviceHealth.length}
                </p>
                {unhealthyDevices > 0 && (
                  <p className="text-sm text-amber-600 font-medium">
                    {unhealthyDevices} need attention
                  </p>
                )}
              </div>
              <div
                className={`p-3 rounded-full ${unhealthyDevices > 0 ? "bg-amber-100" : "bg-green-100"}`}
              >
                <HardDrive
                  className={`w-6 h-6 ${unhealthyDevices > 0 ? "text-amber-600" : "text-green-600"}`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="border-border shadow-sm"
          data-testid="leaked-passwords-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Leaked Passwords
                </p>
                <p className="font-heading text-3xl font-bold text-red-600">
                  {passwords.filter((p) => p.is_leaked).length}
                </p>
                <p className="text-sm text-muted-foreground">
                  Found in breaches
                </p>
              </div>
              <div className="p-3 rounded-full bg-red-100">
                <ShieldAlert className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="backups" data-testid="tab-backups">
            Backups
          </TabsTrigger>
          <TabsTrigger value="passwords" data-testid="tab-passwords">
            Passwords
          </TabsTrigger>
          <TabsTrigger value="storage" data-testid="tab-storage">
            Storage & Health
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Critical Issues */}
          {(backupFailures > 0 ||
            passwordsAtRisk > 0 ||
            unhealthyDevices > 0) && (
            <Card className="border-red-200 bg-red-50 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-semibold text-red-800 flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5" />
                  Critical Issues Requiring Attention
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {passwords
                  .filter((p) => p.is_leaked)
                  .map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-red-200"
                    >
                      <XCircle className="w-5 h-5 text-red-600" />
                      <div className="flex-1">
                        <p className="font-medium text-red-800">
                          Password Leaked: {p.user_email}
                        </p>
                        <p className="text-sm text-red-600">
                          {p.leak_source} - Change immediately!
                        </p>
                      </div>
                      <Badge className="bg-red-100 text-red-800">
                        Critical
                      </Badge>
                    </div>
                  ))}
                {backups
                  .filter((b) => b.status === "failed")
                  .map((b) => (
                    <div
                      key={b.id}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-red-200"
                    >
                      <XCircle className="w-5 h-5 text-red-600" />
                      <div className="flex-1">
                        <p className="font-medium text-red-800">
                          Backup Failed: {b.device_name}
                        </p>
                        <p className="text-sm text-red-600">
                          {b.error_message}
                        </p>
                      </div>
                      <Badge className="bg-red-100 text-red-800">Failed</Badge>
                    </div>
                  ))}
                {deviceHealth
                  .filter((d) => d.health_status === "critical")
                  .map((d) => (
                    <div
                      key={d.id}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-red-200"
                    >
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <div className="flex-1">
                        <p className="font-medium text-red-800">
                          Device Critical: {d.device_name}
                        </p>
                        <p className="text-sm text-red-600">
                          {d.issues.join(", ")}
                        </p>
                      </div>
                      <Badge className="bg-red-100 text-red-800">
                        Critical
                      </Badge>
                    </div>
                  ))}
              </CardContent>
            </Card>
          )}

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Backups */}
            <Card className="border-border shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-semibold">
                  Recent Backups
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {backups.slice(0, 4).map((backup) => (
                    <div
                      key={backup.id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        {getStatusIcon(backup.status)}
                        <div>
                          <p className="font-medium text-sm">
                            {backup.device_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {backup.backup_type} • {backup.size_gb}GB
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={getStatusBadge(backup.status)}>
                          {backup.status}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTimeAgo(backup.last_backup)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Password Recommendations */}
            <Card className="border-border shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-semibold">
                  Password Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {passwords
                    .filter((p) => p.risk_level !== "low")
                    .slice(0, 4)
                    .map((pwd) => (
                      <div
                        key={pwd.id}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          {getStatusIcon(pwd.risk_level)}
                          <div>
                            <p className="font-medium text-sm">
                              {pwd.user_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {pwd.user_email}
                            </p>
                          </div>
                        </div>
                        <div className="text-right max-w-[200px]">
                          <Badge className={getStatusBadge(pwd.risk_level)}>
                            {pwd.risk_level}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {pwd.recommendation}
                          </p>
                        </div>
                      </div>
                    ))}
                  {passwords.filter((p) => p.risk_level !== "low").length ===
                    0 && (
                    <div className="text-center py-4 text-muted-foreground">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
                      <p>All passwords are secure!</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Backups Tab */}
        <TabsContent value="backups">
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold">
                Backup Status
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Device</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Last Backup</TableHead>
                    <TableHead>Next Scheduled</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.map((backup) => (
                    <TableRow
                      key={backup.id}
                      data-testid={`backup-row-${backup.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(backup.status)}
                          <span className="font-medium">
                            {backup.device_name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {backup.backup_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(backup.status)}>
                          {backup.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{backup.size_gb} GB</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="w-3 h-3" />
                          {formatTimeAgo(backup.last_backup)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <RefreshCw className="w-3 h-3" />
                          {formatTimeAgo(backup.next_scheduled)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Passwords Tab */}
        <TabsContent value="passwords">
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold">
                Password Health
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Strength</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead>Leaked</TableHead>
                    <TableHead>Risk Level</TableHead>
                    <TableHead>Recommendation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {passwords.map((pwd) => (
                    <TableRow
                      key={pwd.id}
                      data-testid={`password-row-${pwd.id}`}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium">{pwd.user_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {pwd.user_email}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            pwd.strength === "strong"
                              ? "bg-green-100 text-green-800"
                              : pwd.strength === "medium"
                                ? "bg-amber-100 text-amber-800"
                                : "bg-red-100 text-red-800"
                          }
                        >
                          {pwd.strength}
                        </Badge>
                      </TableCell>
                      <TableCell>{pwd.password_age_days} days</TableCell>
                      <TableCell>
                        {pwd.is_leaked ? (
                          <div>
                            <Badge className="bg-red-100 text-red-800">
                              YES
                            </Badge>
                            <p className="text-xs text-red-600 mt-1">
                              {pwd.leak_source}
                            </p>
                          </div>
                        ) : (
                          <Badge className="bg-green-100 text-green-800">
                            No
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(pwd.risk_level)}>
                          {pwd.risk_level}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[250px]">
                        <p className="text-sm truncate">{pwd.recommendation}</p>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Storage & Health Tab */}
        <TabsContent value="storage">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {deviceHealth.map((device) => (
              <Card
                key={device.id}
                className={`border-border shadow-sm ${device.health_status === "critical" ? "border-red-300" : device.health_status === "warning" ? "border-amber-300" : ""}`}
                data-testid={`device-health-${device.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                      {getStatusIcon(device.health_status)}
                      {device.device_name}
                    </CardTitle>
                    <Badge className={getStatusBadge(device.health_status)}>
                      {device.health_status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* CPU */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Cpu className="w-4 h-4" /> CPU
                      </span>
                      <span
                        className={
                          device.cpu_usage > 80
                            ? "text-red-600 font-medium"
                            : ""
                        }
                      >
                        {device.cpu_usage}%
                      </span>
                    </div>
                    <Progress
                      value={device.cpu_usage}
                      className={`h-2 ${device.cpu_usage > 80 ? "[&>div]:bg-red-500" : device.cpu_usage > 60 ? "[&>div]:bg-amber-500" : ""}`}
                    />
                  </div>

                  {/* Memory */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <MemoryStick className="w-4 h-4" /> Memory
                      </span>
                      <span
                        className={
                          device.memory_usage > 80
                            ? "text-red-600 font-medium"
                            : ""
                        }
                      >
                        {device.memory_usage}%
                      </span>
                    </div>
                    <Progress
                      value={device.memory_usage}
                      className={`h-2 ${device.memory_usage > 80 ? "[&>div]:bg-red-500" : device.memory_usage > 60 ? "[&>div]:bg-amber-500" : ""}`}
                    />
                  </div>

                  {/* Disk */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <HardDrive className="w-4 h-4" /> Storage
                      </span>
                      <span
                        className={
                          device.disk_usage > 80
                            ? "text-red-600 font-medium"
                            : ""
                        }
                      >
                        {device.disk_used_gb}GB / {device.disk_total_gb}GB (
                        {device.disk_usage}%)
                      </span>
                    </div>
                    <Progress
                      value={device.disk_usage}
                      className={`h-2 ${device.disk_usage > 80 ? "[&>div]:bg-red-500" : device.disk_usage > 60 ? "[&>div]:bg-amber-500" : ""}`}
                    />
                  </div>

                  {/* Temperature */}
                  {device.temperature && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Thermometer className="w-4 h-4" /> Temperature
                      </span>
                      <span
                        className={
                          device.temperature > 70
                            ? "text-red-600 font-medium"
                            : ""
                        }
                      >
                        {device.temperature}°C
                      </span>
                    </div>
                  )}

                  {/* Issues */}
                  {device.issues.length > 0 && (
                    <div className="pt-2 border-t">
                      <p className="text-sm font-medium text-red-700 mb-1">
                        Issues:
                      </p>
                      <ul className="text-sm text-red-600 space-y-1">
                        {device.issues.map((issue, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Uptime */}
                  <div className="text-xs text-muted-foreground pt-2 border-t">
                    Uptime: {Math.floor(device.uptime_hours / 24)} days,{" "}
                    {device.uptime_hours % 24} hours • Updated:{" "}
                    {formatTimeAgo(device.last_updated)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
